// Single source of truth for SOLVENDA products, pricing and the plan calculator.
// Subscriptions are billed in EUR; SolvendaConnect transaction fees are in ALL.

import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import RouterIcon from '@mui/icons-material/Router';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HubIcon from '@mui/icons-material/Hub';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DnsIcon from '@mui/icons-material/Dns';
import HistoryIcon from '@mui/icons-material/History';

import { moduleColors } from '../theme';

// --- Pricing constants (EUR / month) ---------------------------------------
export const PRICES = {
  internet: 50, // Solvenda Core internet engine
  radius: 80, // SolvendaRadius engine (replaces internet)
  iptv: 50, // per IPTV platform (Stalker or Xtream Codes)
  dvbc: 50,
  fiscal: 30,
  dns: 15, // DNS / domain filtering add-on
  logs: 20, // Logs History (legal retention) add-on
  olt: 30, // per OLT device
};

// --- SolvendaConnect transaction fee (ALL) ---------------------------------
// < 5000 ALL  -> 50 ALL flat per successful payment
// >= 5000 ALL -> 2% of amount + 20 ALL
export const CONNECT_THRESHOLD = 5000;
export function connectFee(amountAll) {
  const amount = Number(amountAll) || 0;
  if (amount <= 0) return 0;
  if (amount < CONNECT_THRESHOLD) return 50;
  return Math.round(amount * 0.02 + 20);
}

// --- Product catalog (mirrors backend SolvendaProduct codes) ----------------
export const modules = [
  {
    id: 'core',
    code: 'Core',
    name: 'Solvenda Core',
    tagline: 'Billing & management for Internet, IPTV and DVB-C',
    priceLabel: '€50',
    priceSuffix: '/mo per service',
    color: moduleColors.core,
    Icon: DashboardIcon,
    type: 'engine',
    summary:
      'The complete operations platform: clients, operators, regions, services, payments, invoicing and reporting — for ISPs already running Radius Manager, Stalker, Xtream Codes or DVB-C/Conax.',
    features: [
      'Dashboard & advanced reports',
      'Regions & operators',
      'Clients, packets & offers',
      'Services & payments',
      'Withdrawals & watch activity',
      'Warehouse: items, purchase & sale invoices',
    ],
    compatibility: ['Radius Manager', 'DMA', 'MikroTik', 'Stalker Portal', 'Xtream Codes', 'TVCAS', 'Conax'],
  },
  {
    id: 'radius',
    code: 'FreeRadius',
    name: 'SolvendaRadius',
    tagline: 'A modern FreeRadius-based internet engine',
    priceLabel: '€80',
    priceSuffix: '/mo',
    color: moduleColors.radius,
    Icon: RouterIcon,
    type: 'engine',
    badge: 'Replaces Internet',
    summary:
      'Run your internet service with no external software — just Solvenda. FreeRadius user management, speed profiles and NAS integration, built in. Replaces the normal Internet module.',
    features: [
      'FreeRadius user management',
      'Speed profiles & internet packets',
      'NAS / MikroTik integration',
      'Expiry & payment-based control',
      'Reports & watch activity',
      'Warehouse: items, purchase & sale invoices',
    ],
    compatibility: ['FreeRADIUS', 'MikroTik', 'NAS devices', 'DNS Servers'],
  },
  {
    id: 'connect',
    code: 'OnlinePayment',
    name: 'SolvendaConnect',
    tagline: 'Let customers pay online by card',
    priceLabel: '€0',
    priceSuffix: '/mo',
    color: moduleColors.connect,
    Icon: CreditScoreIcon,
    type: 'addon',
    badge: 'No monthly fee',
    external: 'https://connect.solvenda.org',
    summary:
      'A hosted card-payment page for your subscribers at connect.solvenda.org. No monthly fee — you only pay a small fee per successful payment.',
    features: [
      'Online card payment page',
      'Gateway payments & status',
      'Store locations & receipts',
      'Support tickets & account review',
      'Synced with Solvenda payments',
    ],
  },
  {
    id: 'fiscal',
    code: null, // à-la-carte feature, not a standalone license product
    name: 'Solvenda Fiscalization',
    tagline: 'Fiscalize payments directly from Solvenda',
    priceLabel: '€30',
    priceSuffix: '/mo',
    color: moduleColors.fiscal,
    Icon: ReceiptLongIcon,
    type: 'addon',
    summary:
      'Issue compliant Albanian fiscal invoices for the payments you already take. Send them in bulk, and let your operators fiscalize automatically as they collect.',
    features: [
      'Fiscalize payments in one click',
      'Bulk fiscalization',
      'Operator auto-fiscalization',
      'Fiscal invoice tracking & errors',
      'Integrated with your payment flow',
    ],
  },
  {
    id: 'olt',
    code: 'Olt',
    name: 'Solvenda OLT',
    tagline: 'OLT & ONU management, built in',
    priceLabel: '€30',
    priceSuffix: '/mo per OLT',
    color: moduleColors.olt,
    Icon: HubIcon,
    type: 'addon',
    badge: 'ZTE ready',
    summary:
      'Full OLT management — uplinks, VLANs, speed profiles, ONU/ONT signals and provisioning — without leaving Solvenda. ZTE C320, C300 and C600 supported today; Huawei coming soon.',
    features: [
      'OLT dashboard & devices',
      'ONUs & unauthorized ONUs',
      'ONU types & speed profiles',
      'Signals, reboot & provisioning',
      'VLANs, uplinks & service config',
    ],
    compatibility: ['ZTE C320', 'ZTE C300', 'ZTE C600', 'Huawei (soon)'],
  },
  {
    id: 'dns',
    code: null, // à-la-carte feature add-on
    name: 'Solvenda DNS',
    tagline: 'DNS control & domain filtering',
    priceLabel: '€15',
    priceSuffix: '/mo',
    color: moduleColors.dns,
    Icon: DnsIcon,
    type: 'addon',
    summary:
      'Run your own DNS and decide what your subscribers can reach. Manage DNS servers, apply domain restrictions and keep allow/block lists per region.',
    features: [
      'DNS server management',
      'Domain restriction & filtering',
      'Works with any engine',
      'Watch activity & logs',
    ],
  },
  {
    id: 'logs',
    code: null, // à-la-carte feature add-on
    name: 'Logs History',
    tagline: 'Legal log retention for up to 2 years',
    priceLabel: '€20',
    priceSuffix: '/mo',
    color: moduleColors.logs,
    Icon: HistoryIcon,
    type: 'addon',
    badge: 'Compliance',
    summary:
      'Stay compliant with retention rules. We configure a storage server and integrate it with Solvenda, with the right filters on your MikroTik or Cisco NAS, so you can keep and search connection logs for 2 years and beyond.',
    features: [
      'Firewall connection logs (FW-CONN, Src-Dst)',
      'Per-NAS log collection',
      'MikroTik & Cisco filter setup',
      'Storage server integration',
      'Retain & search for 2+ years',
    ],
    compatibility: ['MikroTik', 'Cisco', 'NAS devices'],
  },
  {
    id: 'full',
    code: 'Full',
    name: 'All-in-One',
    tagline: 'Everything Solvenda offers, unlocked',
    priceLabel: 'Custom',
    priceSuffix: '',
    color: moduleColors.full,
    Icon: AutoAwesomeIcon,
    type: 'bundle',
    badge: 'Best value',
    summary:
      'One license that unlocks every module — Core, SolvendaRadius, Fiscalization, OLT and Connect. Ideal for larger ISPs that want the full platform.',
    features: [
      'Every Solvenda module included',
      'SolvendaRadius internet engine',
      'Fiscalization & OLT management',
      'Online payments included',
      'Priority onboarding & support',
    ],
  },
];

export const getModule = (id) => modules.find((m) => m.id === id);

// --- Calculator presets -----------------------------------------------------
// state shape:
// { engine: 'internet'|'radius', iptvStalker, iptvXtream, dvbc, fiscal, dns, connect, oltCount }
const baseState = {
  engine: 'internet',
  iptvStalker: false,
  iptvXtream: false,
  dvbc: false,
  fiscal: false,
  dns: false,
  logs: false,
  connect: true,
  oltCount: 0,
};

export const presets = [
  {
    id: 'starter',
    label: 'Starter',
    description: 'Internet only',
    state: { ...baseState, engine: 'internet' },
  },
  {
    id: 'popular',
    label: 'Most Popular',
    description: 'Internet + IPTV + Fiscalization',
    highlight: true,
    state: { ...baseState, engine: 'internet', iptvStalker: true, fiscal: true },
  },
  {
    id: 'radius',
    label: 'Radius',
    description: 'SolvendaRadius + Fiscalization',
    state: { ...baseState, engine: 'radius', fiscal: true },
  },
  {
    id: 'all',
    label: 'All-in-One',
    description: 'Everything + 1 OLT',
    state: {
      ...baseState,
      engine: 'radius',
      iptvStalker: true,
      iptvXtream: true,
      dvbc: true,
      fiscal: true,
      dns: true,
      logs: true,
      oltCount: 1,
    },
  },
];

export function computeTotal(state) {
  const lines = [];
  if (state.engine === 'radius') {
    lines.push({ label: 'SolvendaRadius', price: PRICES.radius });
  } else {
    lines.push({ label: 'Solvenda Internet', price: PRICES.internet });
  }
  if (state.iptvStalker) lines.push({ label: 'IPTV — Stalker', price: PRICES.iptv });
  if (state.iptvXtream) lines.push({ label: 'IPTV — Xtream Codes', price: PRICES.iptv });
  if (state.dvbc) lines.push({ label: 'DVB-C', price: PRICES.dvbc });
  if (state.fiscal) lines.push({ label: 'Fiscalization', price: PRICES.fiscal });
  if (state.dns) lines.push({ label: 'Solvenda DNS', price: PRICES.dns });
  if (state.logs) lines.push({ label: 'Logs History', price: PRICES.logs });
  if (state.oltCount > 0)
    lines.push({ label: `OLT Management × ${state.oltCount}`, price: PRICES.olt * state.oltCount });
  if (state.connect)
    lines.push({ label: 'SolvendaConnect', price: 0, note: 'per-transaction' });

  const total = lines.reduce((sum, l) => sum + l.price, 0);
  return { lines, total };
}
